import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Input, Modal, Select, Spin, Tag, message } from "antd";
import { PaperClipOutlined, ReloadOutlined } from "@ant-design/icons";

import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import ReferencePicker from "./ReferencePicker";
import EmailBody from "../CRM/EmailBody";
import {
  composeFtcEmail,
  getFtcConversations,
  getFtcMailOffices,
  getFtcThread,
  markFtcThreadRead,
  replyFtcEmail,
} from "./controller";
import "./ftcInbox.css";

/**
 * The FTC's inbox — correspondence with the clinics they are assigned to.
 *
 * Deliberately NOT a general mail client. An FTC can only write to contacts a
 * super-admin approved, so the recipient field is a picker over those contacts
 * rather than free text: an address they may not use should not be typeable in
 * the first place. The server re-checks regardless (crm.services.ftc_mail) —
 * this is convenience, not the boundary.
 */

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function FtcInboxPage() {
  const [loading, setLoading] = useState(true);
  const [mailbox, setMailbox] = useState(null);
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState(null);

  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);

  // A failed load must not render as "nobody assigned you anything" — that reads
  // as a settled fact about the account and sends the FTC to ask an admin for
  // something they already have.
  const [loadFailed, setLoadFailed] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replyRefs, setReplyRefs] = useState([]);
  const [sending, setSending] = useState(false);

  const loadConversations = useCallback(
    async function (oid) {
      const res = await getFtcConversations(oid);
      const list = (res.json && res.json.conversations) || [];
      list.sort((a, b) => String(b.last_at || "").localeCompare(String(a.last_at || "")));
      setConversations(list);
    },
    [],
  );

  useEffect(function () {
    let cancelled = false;
    (async function () {
      setLoading(true);
      const res = await getFtcMailOffices();
      if (cancelled) return;
      const ok = res.status >= 200 && res.status < 300 && res.json;
      setLoadFailed(!ok);
      const body = ok ? res.json : {};
      setMailbox(body.mailbox || null);
      setOffices(body.offices || []);
      await loadConversations(null);
      if (!cancelled) setLoading(false);
    })();
    return function () { cancelled = true; };
  }, [loadConversations]);

  const openThread = useCallback(
    async function (conv) {
      setActiveConv(conv);
      setReplyRefs([]);
      setThreadLoading(true);
      const res = await getFtcThread(conv.conversation_id);
      setThread(Array.isArray(res.json) ? res.json : []);
      setThreadLoading(false);
      if (conv.unread_count) {
        await markFtcThreadRead(conv.conversation_id);
        // Reflect it locally rather than refetching the whole list for one badge.
        setConversations(function (prev) {
          return prev.map((c) =>
            c.conversation_id === conv.conversation_id ? { ...c, unread_count: 0 } : c);
        });
      }
    },
    [],
  );

  async function sendReply() {
    if (!activeConv || !replyBody.trim()) return;
    setSending(true);
    try {
      const res = await replyFtcEmail({
        conversation_id: activeConv.conversation_id,
        body_html: replyBody,
        references: replyRefs.map((r) => ({ kind: r.kind, id: r.id })),
      });
      if (res.status >= 200 && res.status < 300) {
        setReplyBody("");
        setReplyRefs([]);
        message.success("Reply sent.");
        openThread(activeConv);
      } else {
        message.error((res.json && res.json.detail) || "Couldn't send the reply.");
      }
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="ftcinbox-center"><Spin /></div>
      </DashboardLayout>
    );
  }

  // Two distinct empty states: no mailbox is an admin task, no assignment is a
  // different admin task. Collapsing them into "nothing here" would leave the FTC
  // with no idea who to ask.
  if (loadFailed) {
    return (
      <DashboardLayout>
        <div className="ftcinbox-center">
          <Empty
            description={
              <>
                <div>Couldn&rsquo;t load your inbox.</div>
                <div className="ftcinbox-sub">
                  Something went wrong on the server — your access may well be fine.
                </div>
              </>
            }
          >
            <Button onClick={() => window.location.reload()}>Try again</Button>
          </Empty>
        </div>
      </DashboardLayout>
    );
  }

  if (!mailbox) {
    return (
      <DashboardLayout>
        <div className="ftcinbox-center">
          <Empty description="You don't have a Smilepass mailbox yet. Ask an admin to set one up." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="ftcinbox">
        <div className="ftcinbox-head">
          <div>
            <h2 className="ftcinbox-title">Inbox</h2>
            <p className="ftcinbox-sub">Sending as {mailbox}</p>
          </div>
          <div className="ftcinbox-head-right">
            <Select
              allowClear
              placeholder="All clinics"
              value={officeId}
              onChange={(v) => { setOfficeId(v || null); setActiveConv(null); loadConversations(v || null); }}
              options={offices.map((o) => ({ value: o.id, label: o.name }))}
              style={{ width: 220 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => loadConversations(officeId)} />
            <Button
              type="primary"
              disabled={!offices.length}
              onClick={() => { setOfficeId(officeId || (offices[0] && offices[0].id)); setComposeOpen(true); }}
            >
              New email
            </Button>
          </div>
        </div>

        {!offices.length ? (
          <Empty description="No clinics are assigned to you yet. Ask an admin to assign one." />
        ) : (
          <div className="ftcinbox-body">
            <div className="ftcinbox-list">
              {!conversations.length ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No conversations yet." />
              ) : (
                conversations.map((c) => (
                  <button
                    type="button"
                    key={c.conversation_id}
                    className={
                      "ftcinbox-conv" +
                      (activeConv && activeConv.conversation_id === c.conversation_id ? " is-active" : "") +
                      (c.unread_count ? " is-unread" : "")
                    }
                    onClick={() => openThread(c)}
                  >
                    <div className="ftcinbox-conv-top">
                      <span className="ftcinbox-conv-subject">{c.subject || "(no subject)"}</span>
                      <span className="ftcinbox-conv-when">{when(c.last_at)}</span>
                    </div>
                    <div className="ftcinbox-conv-preview">{c.last_preview}</div>
                    <div className="ftcinbox-conv-meta">
                      {c.unread_count ? <Tag color="red">{c.unread_count} new</Tag> : null}
                      <span>{c.message_count} msg</span>
                      {c.has_attachments ? <PaperClipOutlined /> : null}
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="ftcinbox-thread">
              {!activeConv ? (
                <div className="ftcinbox-center">
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                         description="Pick a conversation to read it." />
                </div>
              ) : threadLoading ? (
                <div className="ftcinbox-center"><Spin /></div>
              ) : (
                <>
                  <div className="ftcinbox-thread-head">{activeConv.subject || "(no subject)"}</div>
                  <div className="ftcinbox-messages">
                    {thread.map((m) => (
                      <div
                        key={m.id}
                        className={"ftcinbox-msg" + (m.direction === "outbound" ? " is-ours" : "")}
                      >
                        <div className="ftcinbox-msg-meta">
                          <strong>{m.from_address || (m.direction === "outbound" ? "You" : "")}</strong>
                          <span>{when(m.sent_at || m.received_at)}</span>
                        </div>
                        <div className="ftcinbox-msg-body">
                          <EmailBody html={m.body_html} preview={m.body_preview} />
                        </div>
                        {(m.attachments || []).map((a) => (
                          <a key={a.id} className="ftcinbox-att" href={a.download_url}
                             target="_blank" rel="noopener noreferrer">
                            <PaperClipOutlined /> {a.filename}
                          </a>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="ftcinbox-reply">
                    <Input.TextArea
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      placeholder="Write a reply…"
                      autoSize={{ minRows: 3, maxRows: 8 }}
                    />
                    <ReferencePicker
                      officeId={activeConv.office_id}
                      value={replyRefs}
                      onChange={setReplyRefs}
                      disabled={sending}
                    />
                    <Button type="primary" loading={sending}
                            disabled={!replyBody.trim()} onClick={sendReply}>
                      Send reply
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <ComposeModal
          open={composeOpen}
          onClose={() => setComposeOpen(false)}
          offices={offices}
          defaultOfficeId={officeId || (offices[0] && offices[0].id)}
          onSent={() => { setComposeOpen(false); loadConversations(officeId); }}
        />
      </div>
    </DashboardLayout>
  );
}

function ComposeModal({ open, onClose, offices, defaultOfficeId, onSent }) {
  const [officeId, setOfficeId] = useState(defaultOfficeId);
  const [to, setTo] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [refs, setRefs] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(function () {
    if (open) {
      setOfficeId(defaultOfficeId);
      setTo([]);
      setSubject("");
      setBody("");
      setRefs([]);
    }
  }, [open, defaultOfficeId]);

  const office = offices.find((o) => o.id === officeId) || null;
  const contacts = (office && office.contacts) || [];

  async function send() {
    setSending(true);
    try {
      const res = await composeFtcEmail({
        office_id: officeId, to, subject, body_html: body,
        references: refs.map((r) => ({ kind: r.kind, id: r.id })),
      });
      if (res.status >= 200 && res.status < 300) {
        message.success("Email sent.");
        onSent();
      } else {
        message.error((res.json && res.json.detail) || "Couldn't send the email.");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal open={open} onCancel={onClose} title="New email" width={640} footer={null}>
      <div className="ftcinbox-field">
        <label>Clinic</label>
        <Select
          value={officeId}
          onChange={(v) => { setOfficeId(v); setTo([]); setRefs([]); }}
          options={offices.map((o) => ({ value: o.id, label: o.name }))}
          style={{ width: "100%" }}
        />
      </div>
      <div className="ftcinbox-field">
        <label>To</label>
        {/* A picker, not free text: an FTC may only write to approved contacts, so
            an address they cannot use should not be typeable. */}
        <Select
          mode="multiple"
          value={to}
          onChange={setTo}
          placeholder={contacts.length ? "Choose contacts" : "No approved contacts at this clinic"}
          disabled={!contacts.length}
          options={contacts.map((c) => ({
            value: c.email,
            label: c.name ? `${c.name} · ${c.email}` : c.email,
          }))}
          style={{ width: "100%" }}
        />
      </div>
      <div className="ftcinbox-field">
        <label>Subject</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div className="ftcinbox-field">
        <label>Message</label>
        <Input.TextArea value={body} onChange={(e) => setBody(e.target.value)}
                        autoSize={{ minRows: 6, maxRows: 14 }} />
      </div>
      <div className="ftcinbox-field">
        <label>Attach from Smilepass</label>
        <ReferencePicker officeId={officeId} value={refs} onChange={setRefs} disabled={sending} />
      </div>
      <div className="ftcinbox-actions">
        <Button onClick={onClose}>Cancel</Button>
        <Button type="primary" loading={sending}
                disabled={!to.length || !subject.trim() || !body.trim()} onClick={send}>
          Send
        </Button>
      </div>
    </Modal>
  );
}
